import { useCallback, useMemo } from 'react';
import { useLocalStorage } from 'react-use';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';

type UseColumnVisibilityOptions = {
  storageKey: string;
  defaults: GridColumnVisibilityModel;
  alwaysVisibleColumns: string[];
};

export const useColumnVisibility = ({
  storageKey,
  defaults,
  alwaysVisibleColumns,
}: UseColumnVisibilityOptions): [
  GridColumnVisibilityModel,
  (model: GridColumnVisibilityModel) => void,
  () => void
] => {
  const [stored, setStored] = useLocalStorage<GridColumnVisibilityModel>(
    storageKey,
    defaults
  );

  const alwaysOn = useMemo(
    () => Object.fromEntries(alwaysVisibleColumns.map((col) => [col, true])),
    [alwaysVisibleColumns]
  );

  const normalized = useMemo(() => {
    return { ...defaults, ...stored, ...alwaysOn };
  }, [defaults, stored, alwaysOn]);

  const set = useCallback(
    (model: GridColumnVisibilityModel) => {
      setStored({ ...defaults, ...model, ...alwaysOn });
    },
    [setStored, defaults, alwaysOn]
  );

  const reset = useCallback(() => setStored(defaults), [setStored, defaults]);

  return [normalized, set, reset];
};
